import { useCallback, type ChangeEvent, type ComponentProps } from "react";
import {
  Divider,
  FormControl,
  FormControlLabel,
  Switch as MUISwitch,
  Typography,
} from "@mui/material";
import { debug as debugFn } from "debug";
import { useField } from "formik";
import { useTranslation } from "react-i18next";

import type { TranslationKeyAndOptions } from "@thalamos/vision-common";
import { translate } from "@thalamos/vision-common";

import { Translation } from "../../Translation/Translation.js";
import { FormError } from "../FormError/FormError.js";

const debug = debugFn("vision-frontend:Switch");

export type SwitchProps = {
  fieldName: string;
  label: TranslationKeyAndOptions;
} & Pick<ComponentProps<typeof MUISwitch>, "autoFocus">;

type SwitchOutput = { value: "yes"; valueLabel: string } | "";

export const Switch = ({ fieldName, label, autoFocus }: SwitchProps) => {
  const { t } = useTranslation();

  const [field, meta, helpers] = useField<SwitchOutput>(fieldName);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const runAsync = async () => {
        const newValue = event.target.checked
          ? ({ value: "yes", valueLabel: translate(t, label) } as const)
          : "";

        debug("Setting value", {
          newValue,
        });
        await helpers.setValue(newValue);
        await helpers.setTouched(true);
      };

      void runAsync();
    },
    [helpers, label, t],
  );

  return (
    <FormControl
      error={!!(meta.touched && meta.error)}
      component="fieldset"
      data-testid={`formField-${fieldName}`}
    >
      <FormControlLabel
        control={
          <MUISwitch
            checked={field.value !== ""}
            onChange={handleChange}
            name={field.name}
            autoFocus={autoFocus}
          />
        }
        label={<Translation {...label} />}
      />
      {meta.touched && meta.error && <FormError error={meta.error} />}
      {debug.enabled && (
        <>
          <Divider />
          <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
            {"field.value : "}
            {JSON.stringify(field.value)}
          </Typography>
          <Divider />
        </>
      )}
    </FormControl>
  );
};
